import { DriveStep } from 'driver.js';

import tourData from './tour-flows.json';

// ──────────────────────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────────────────────

export interface InteractiveStepConfig {
  /**
   * What the user must do to advance this step.
   * - 'click': user clicks the highlighted element
   * - 'input': user types into the highlighted element (advances when validate() passes)
   * - 'select': user picks an option from a portal-rendered popup
   *   (e.g. MUI Autocomplete / Select). Listens at document level via
   *   delegation on `optionSelector`.
   * - 'route-change': the click navigates; do not call moveNext(), let the route effect resume
   * - 'appear': advance once `waitFor` appears, however the user got there
   *   (e.g. a search run by Enter, the Search button, or a tab switch)
   */
  trigger: 'click' | 'input' | 'select' | 'route-change' | 'appear';
  /**
   * Optional CSS selector to wait for after the user action, before advancing
   * (useful when the click opens a modal/drawer whose content is the next step).
   */
  waitFor?: string;
  /** Max time to wait for `waitFor` to appear, in ms. Defaults to 3000. */
  waitForTimeoutMs?: number;
  /**
   * For 'click' trigger: only advance the tour if `waitFor` actually appears.
   * If it times out (the action was blocked — e.g. a gated wizard refusing to
   * proceed until validation exceptions are cleared), the trigger is re-armed
   * so the user can resolve the blocker and click again, instead of the tour
   * desyncing from the screen. Requires `waitFor` to be set.
   */
  requireWaitFor?: boolean;
  /**
   * For 'input' trigger: return true when the input value is acceptable to advance.
   * Defaults to `el.value.trim().length > 0`. Code-only — JSON cannot supply it.
   */
  validate?: (el: HTMLElement) => boolean;
  /**
   * For 'input' and 'appear' triggers: the one value the tour asks the user to
   * type into the step's input. Keystrokes that would make the box anything
   * other than a case-insensitive prefix of it are refused (deleting is always
   * allowed), so the search the next step depends on cannot go wrong. For
   * 'input' it also becomes the default `validate`: the step advances once the
   * whole value is typed, not on the first keystroke.
   */
  expectedValue?: string;
  /**
   * For 'select' trigger: CSS selector a click target must match to advance.
   * Defaults to `.MuiAutocomplete-option`.
   */
  optionSelector?: string;
  /**
   * For 'click' trigger: the element the user must actually click to advance,
   * when it differs from the step's highlighted `element`. Lets a step spotlight
   * a broad work area (e.g. the whole wizard stage content) while advancing only
   * on a specific button click (e.g. the footer "Proceed"). Defaults to the
   * step's `element` selector.
   */
  targetSelector?: string;
  /**
   * Also offer the popover's Next button, so the user can move on without
   * performing the action. For steps where the action depends on data the
   * user may not have to hand (e.g. an employee name to search for). Next
   * honours the following steps' `skipIfMissing`, so a step that needs the
   * action's result can be skipped when it was never performed.
   */
  showNext?: boolean;
}

/**
 * Alternate popover copy shown when the step's target is in an "empty" state
 * (e.g. a data table showing "No data available"). Lets a single step adapt
 * its guidance instead of telling the user to interact with rows that aren't
 * there. See [[tours-no-empty-table-dependency]].
 */
export interface EmptyStatePopover {
  /**
   * Selector whose presence signals the empty state. Defaults to
   * `[data-tour="table-empty-state"]` (the shared DataTable empty row).
   */
  selector?: string;
  /** Title to use when empty. Keeps the step's normal title if omitted. */
  title?: string;
  /** Description to use when empty. */
  description: string;
}

/**
 * Alternate popover copy shown when a selector is ABSENT (the inverse of
 * `emptyState`). Use it where a step's subject depends on the record's state —
 * e.g. the Accept button only renders for an award that still accepts a
 * response — so the step keeps its place in the count and explains the state
 * instead of being skipped.
 */
export interface MissingStatePopover {
  /** Selector whose absence triggers the alternate copy. */
  selector: string;
  title?: string;
  description: string;
}

/**
 * Permission gate, shared by flows and steps: the user needs ANY `accessKey`
 * code or ANY code matching `accessPattern` (the sidebar's rule). With neither
 * set, everyone passes.
 */
export interface AccessGate {
  accessKey?: string[];
  accessPattern?: string;
}

export interface AppTourStep extends DriveStep, AccessGate {
  route: string;
  interactive?: InteractiveStepConfig;
  /**
   * CSS selector that must be present in the DOM for this step to run. If it
   * is absent when the tour reaches the step, the step is skipped. Use for
   * steps that point at conditionally-rendered elements (e.g. Accept / Reject
   * buttons that only appear for certain award statuses).
   */
  skipIfMissing?: string;
  /**
   * Swap the popover copy when the target is empty (e.g. a table with no
   * rows). Evaluated when the popover renders.
   */
  emptyState?: EmptyStatePopover;
  /** Swap the popover copy when a selector is absent (see MissingStatePopover). */
  whenMissing?: MissingStatePopover;
  /**
   * Make the highlighted element non-clickable for this step. Use on terminal
   * action buttons (Submit, Send for Approval, Accept/Reject) whose real click
   * would run a live mutation and/or kick the wizard back to a validation
   * screen — desyncing the tour. The step stays purely informational and the
   * user ends the tour with the popover's Finish button. Implemented via
   * driver.js `disableActiveInteraction` plus restoring the overlay's
   * click-blocking (the tour otherwise lets clicks pass through to the page).
   */
  blockInteraction?: boolean;
  /**
   * When set, clicking the popover's own **Next** button first clicks the
   * element matching this selector, then advances the tour. Use to drive a
   * single-route wizard forward from the popover (e.g. the User Onboarding
   * "Next" button) so the user advances via the tour's Next instead of hunting
   * for the dimmed page button. The step stays informational (popover keeps its
   * Next), but the page is driven in lockstep.
   */
  advanceClick?: string;
  /**
   * When Back returns to this step from the next one, first click the element
   * matching this selector to reverse the step's own action, then wait for the
   * step's target to come back. Use on a step whose action moves a wizard on
   * and removes the step's target (e.g. a Next button that only exists on the
   * first stage); without it Back lands on a popover with nothing to point at.
   */
  undoClick?: string;
  /**
   * Controls on a view the tour steers (the Administration Hub's cards and
   * module tabs) that the user may click on this step, matched against the
   * clicked control itself. The control the step highlights is always
   * clickable; use this when the step spotlights a group (e.g. every card)
   * and invites the user to pick one.
   */
  allowClick?: string;
}

/**
 * Who a flow is written for. End users (employees) see only their own records
 * and have no admin controls, so a screen they share with admins gets its own
 * end-user flow — separate steps, separate id, separately tracked progress.
 */
export type TourRole = 'admin' | 'endUser';

/**
 * Backup steps for when the data the main steps rely on is not there (e.g. no
 * award is "Action Required", so there is nothing to open and respond to).
 * Decided once, on the flow's first page, after its data has loaded and before
 * any step is shown — so the run's "Step x of y" never changes mid-tour.
 */
export interface FlowFallback {
  /** Present → run the main `steps`; absent → run these `steps`. */
  requires: string;
  /**
   * Present once the page has finished loading the data `requires` is looked
   * up in (rows OR the empty state), so the check never runs against a loader.
   * Defaults to `requires`.
   */
  readyWhen?: string;
  /** Max wait for `readyWhen`, in ms. Defaults to 8000. */
  timeoutMs?: number;
  /** Must start on the same route as the main steps. */
  steps: AppTourStep[];
}

export type FlowVariant = 'main' | 'fallback';

export interface TourFlow extends AccessGate {
  id: string;
  title: string;
  description: string;
  category: string;
  /** MUI icon name — looked up in the Help Center page's iconMap */
  icon: string;
  /** Route the tour kicks off on */
  startRoute: string;
  /**
   * History state to open `startRoute` with, for pages that keep their view in
   * `location.state` — e.g. `{ dashboardSection: 'awards', activeTab:
   * 'share_credit' }` opens the Administration Hub on the Share Crediting tab
   * rather than on whichever card the user last had open. Applied whenever the
   * run starts or resumes on `startRoute`, replacing the state there.
   */
  startState?: Record<string, unknown>;
  /**
   * Which application module this tour belongs to. The platform **admin**
   * module (client/admin-member management) and the **tenant** module (a client
   * company's workspace) each have their own Help Center, so the Help Center
   * only lists tours whose `module` matches the module it is rendered in.
   * Defaults to `'tenant'` when omitted.
   */
  module?: 'admin' | 'tenant';
  /**
   * Human-readable audience label shown as a chip on the Help Center card and
   * in the printed guide, e.g. "Platform Admin", "Tenant Admin", "Employee",
   * "All Users". Purely descriptive — actual gating is done by `accessKey`.
   */
  audience?: string;
  /** Marks the flow as having interactive (click/input) steps */
  hasInteractive?: boolean;
  /** Approximate completion time, used for the "X Mins" chip */
  estimatedMinutes?: number;
  /** Short labels shown in the card's REQUIREMENTS row */
  requirements?: string[];
  /**
   * Roles the flow is listed for. Omitted → every role. Checked before the
   * permission gate: a flow for another role is never shown, not even locked.
   */
  roles?: TourRole[];
  /**
   * When true, the Help Center still renders this flow's card for users who
   * lack the `accessKey` permissions instead of hiding it — the card is shown
   * in a disabled (locked) state. Used by the "Award flow" journey so the full
   * end-to-end sequence of phases is always visible, with the phases the user
   * cannot access greyed out. Without this flag, a flow the user lacks
   * permission for is filtered out entirely.
   */
  showWhenLocked?: boolean;
  /**
   * Single-route stepper wizards (e.g. Create Award, User Onboarding) live
   * entirely on one static route and remount at stage 1 whenever the page
   * loads. Mark them so that, on resume, the tour first drives the wizard's
   * stepper to the stage the saved step belongs to (by clicking the matching
   * stage label) before highlighting it — otherwise a deep saved step would
   * land on a late popover while the form sits back on stage 1. The target
   * stage is derived from how many interactive "Next" steps precede the saved
   * step. See [[tour-gated-stepper-requirewaitfor]].
   */
  stepperWizard?: boolean;
  /**
   * Suppress plain row-click navigation on this flow's list page while the tour
   * runs, so the user follows the row's three-dot menu (which the tour guides)
   * instead of accidentally navigating to a read-only preview that dead-ends the
   * tour. The engine toggles a `tour-suppress-row-nav` body class for flows with
   * this flag; the list page's row-click handler checks for it. Leave it off for
   * flows that legitimately navigate via row click (e.g. a preview tour).
   */
  suppressRowNav?: boolean;
  /** Backup steps for when the main steps' data is missing. */
  fallback?: FlowFallback;
  steps: AppTourStep[];
}

// ──────────────────────────────────────────────────────────────────────────────
// JSON registry — flows live in tour-flows.json so they can move to a backend
// API later without code changes. The cast is safe because the JSON is shaped
// by hand to match TourFlow; runtime structure is validated implicitly when
// the tour runs (missing fields would surface immediately in driver.js).
// ──────────────────────────────────────────────────────────────────────────────
export const tourFlows: TourFlow[] = tourData.flows as TourFlow[];

export const getTourFlow = (id: string): TourFlow | undefined =>
  tourFlows.find(f => f.id === id);

/**
 * The tour role for a profile. Mirrors the pages, which render the admin
 * controls (employee search, tabs) only for `isAdmin === 'Admin'` — anything
 * else gets the end-user screen, so it must get the end-user tours too.
 */
export const getTourRole = (isAdmin?: string): TourRole =>
  isAdmin === 'Admin' ? 'admin' : 'endUser';

export const isFlowForRole = (flow: TourFlow, role: TourRole): boolean =>
  !flow.roles?.length || flow.roles.includes(role);

export interface TourAccess {
  hasAnyPermission: (codes: string[]) => boolean;
  permissionCodes: string[];
}

export const passesGate = (gate: AccessGate, access: TourAccess): boolean => {
  const keys = gate.accessKey ?? [];
  if (!keys.length && !gate.accessPattern) return true;
  if (keys.length && access.hasAnyPermission(keys)) return true;
  if (!gate.accessPattern) return false;
  const regex = new RegExp(gate.accessPattern);
  return access.permissionCodes.some(code => regex.test(code));
};

/**
 * The steps this user will actually run: the chosen variant, minus steps
 * their permissions exclude. Resolved before the tour starts, so the count
 * is known up front and stays fixed for the whole run.
 */
export const resolveFlowSteps = (
  flow: TourFlow,
  access: TourAccess,
  variant: FlowVariant = 'main'
): AppTourStep[] => {
  const steps =
    variant === 'fallback' && flow.fallback ? flow.fallback.steps : flow.steps;
  return steps.filter(step => passesGate(step, access));
};
